import { isApiError } from "@pokifolio/api-client";
import { MAX_PHOTOS_PER_CARD, PHOTO_TYPES, type PortfolioItem } from "@pokifolio/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Trash2 } from "lucide-react";
import { type ChangeEvent, useId, useState } from "react";

import { useApi } from "../../app/api-context";
import { Alert, ErrorAlert } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { describeError, serverFieldError } from "../../lib/errors";
import { useHasPermission } from "../auth/session";
import { Fact } from "../research/fact";
import { formatDate } from "../research/format";
import { formatMoney } from "../search/format";
import { describeCondition, describeSet } from "./format";
import { valuesOf } from "./card-values";
import { PortfolioForm } from "./portfolio-form";
import { usePortfolioChange, usePortfolioItem } from "./portfolio-queries";

const backLink = (
  <Link to="/portfolio" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
    <ArrowLeft className="size-4" aria-hidden="true" />
    Portfolio
  </Link>
);

/** One card: photos, details, edit and remove. */
export function PortfolioItemPage({ itemId }: { itemId: string }) {
  const item = usePortfolioItem(itemId);
  const canChange = useHasPermission("holdings.update");

  if (item.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {backLink}
        <Card>
          <p className="text-sm text-muted-foreground">Loading the card…</p>
        </Card>
      </div>
    );
  }
  if (item.isError) {
    const notFound = isApiError(item.error) && (item.error.status === 404 || item.error.status === 422);
    return (
      <div className="flex flex-col gap-4">
        {backLink}
        {notFound ? (
          <Card>
            <h1 className="text-lg font-semibold text-foreground">This card is not in the portfolio.</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              It may have been removed, or the link is old.
            </p>
          </Card>
        ) : (
          <ErrorAlert error={item.error} />
        )}
      </div>
    );
  }
  return <CardView item={item.data} canChange={canChange} />;
}

function CardView({ item, canChange }: { item: PortfolioItem; canChange: boolean }) {
  const api = useApi();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const update = usePortfolioChange((body: Parameters<typeof api.updatePortfolioItem>[1]) =>
    api.updatePortfolioItem(item.id, body),
  );
  const remove = usePortfolioChange(() => api.removePortfolioItem(item.id));
  const set = describeSet(item);

  return (
    <div className="flex flex-col gap-6">
      {backLink}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{item.name}</h1>
          {set !== "" && <p className="mt-1 text-sm text-muted-foreground">{set}</p>}
          <p className="mt-1 text-sm text-muted-foreground">
            Added by {item.addedBy.name} on {formatDate(item.addedAt)}
          </p>
        </div>
        <Badge tone="brand">{describeCondition(item)}</Badge>
      </div>

      <Photos item={item} canChange={canChange} />

      <Card>
        <section aria-labelledby="details-heading" className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id="details-heading" className="text-base font-semibold text-foreground">
              Details
            </h2>
            {canChange && !editing && (
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                Edit details
              </Button>
            )}
          </div>
          {editing ? (
            <PortfolioForm
              initial={valuesOf(item)}
              costHidden={item.costHidden}
              submitLabel="Save changes"
              pending={update.isPending}
              error={update.error}
              onCancel={() => {
                update.reset();
                setEditing(false);
              }}
              onSubmit={(input) => {
                update.mutate({ item: input, version: item.version }, { onSuccess: () => setEditing(false) });
              }}
            />
          ) : (
            <Details item={item} />
          )}
        </section>
      </Card>

      {canChange && (
        <Card>
          <section aria-labelledby="remove-heading" className="flex flex-col gap-3">
            <h2 id="remove-heading" className="text-base font-semibold text-foreground">
              Remove from portfolio
            </h2>
            {remove.isError && <ErrorAlert error={remove.error} />}
            {confirmRemove ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Remove “{item.name}” from the portfolio? It will no longer be listed; its record is kept in
                  the activity log.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={remove.isPending}
                    onClick={() =>
                      remove.mutate(undefined, { onSuccess: () => void navigate({ to: "/portfolio" }) })
                    }
                  >
                    Yes, remove it
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(false)}>
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <div>
                <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(true)}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Remove card…
                </Button>
              </div>
            )}
          </section>
        </Card>
      )}
    </div>
  );
}

function Details({ item }: { item: PortfolioItem }) {
  const empty = <span className="text-muted-foreground">—</span>;
  return (
    <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
      <Fact label="Card type">{item.cardType === "raw" ? "Raw (not graded)" : "Graded"}</Fact>
      <Fact label={item.cardType === "raw" ? "Condition" : "Grade"}>{describeCondition(item)}</Fact>
      {item.cardType === "graded" && (
        <Fact label="Certificate number">{item.certificateNumber ?? empty}</Fact>
      )}
      <Fact label="Language">{item.language ?? empty}</Fact>
      <Fact label="Quantity">{item.quantity}</Fact>
      <Fact label="Purchase price">
        {item.costHidden ? (
          <span className="text-muted-foreground">Only people allowed to see costs can see it</span>
        ) : item.purchasePrice === null || item.purchaseCurrency === null ? (
          empty
        ) : (
          formatMoney({ amount: item.purchasePrice, currency: item.purchaseCurrency })
        )}
      </Fact>
      <Fact label="Purchase date">{item.purchaseDate ?? empty}</Fact>
      <Fact label="Bought from">{item.boughtFrom ?? empty}</Fact>
      <Fact label="Where it is kept">{item.storageLocation ?? empty}</Fact>
      {item.researchId !== null && (
        <Fact label="Research">
          <Link
            to="/research/$researchId"
            params={{ researchId: item.researchId }}
            className="text-primary hover:underline"
          >
            Open the research
          </Link>
        </Fact>
      )}
      {item.notes !== null && (
        <div className="sm:col-span-3">
          <Fact label="Notes">{item.notes}</Fact>
        </div>
      )}
    </dl>
  );
}

/** Photos of the card: add (one or several at once) and remove. */
function Photos({ item, canChange }: { item: PortfolioItem; canChange: boolean }) {
  const api = useApi();
  const inputId = useId();
  const [problems, setProblems] = useState<string[]>([]);
  const upload = usePortfolioChange(async (files: File[]) => {
    const failed: string[] = [];
    for (const file of files) {
      try {
        await api.addPortfolioPhoto(item.id, file);
      } catch (error) {
        // The server says exactly what is wrong with the photo (too large, not an image, …).
        failed.push(`${file.name}: ${serverFieldError(error, "photo") ?? describeError(error).message}`);
      }
    }
    return failed;
  });
  const removePhoto = usePortfolioChange((photoId: string) => api.removePortfolioPhoto(item.id, photoId));
  const room = MAX_PHOTOS_PER_CARD - item.photos.length;

  function choose(event: ChangeEvent<HTMLInputElement>) {
    const files = [...(event.target.files ?? [])];
    event.target.value = "";
    if (files.length === 0) return;
    const skipped = files
      .slice(room)
      .map((f) => `${f.name}: a card can have at most ${String(MAX_PHOTOS_PER_CARD)} photos.`);
    upload.mutate(files.slice(0, room), { onSuccess: (failed) => setProblems([...failed, ...skipped]) });
  }

  return (
    <Card>
      <section aria-labelledby="photos-heading" className="flex flex-col gap-4">
        <h2 id="photos-heading" className="text-base font-semibold text-foreground">
          Photos
        </h2>
        {item.photos.length === 0 ? (
          <p className="text-sm text-muted-foreground">No photos yet.</p>
        ) : (
          <ul aria-label="Photos" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {item.photos.map((photo, index) => (
              <li key={photo.id} className="flex flex-col gap-1">
                <a href={api.portfolioPhotoUrl(item.id, photo.id)} target="_blank" rel="noopener noreferrer">
                  <img
                    src={api.portfolioPhotoUrl(item.id, photo.id)}
                    alt={`Photo ${String(index + 1)} of ${item.name}`}
                    className="aspect-square w-full rounded border border-border object-cover"
                  />
                </a>
                {canChange && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={removePhoto.isPending}
                    onClick={() => removePhoto.mutate(photo.id)}
                    aria-label={`Remove photo ${String(index + 1)}`}
                  >
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {problems.length > 0 && (
          <Alert tone="warning">
            <ul className="flex flex-col gap-1">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Alert>
        )}
        {removePhoto.isError && <ErrorAlert error={removePhoto.error} />}
        {canChange && (
          <div className="flex flex-col gap-1">
            <label
              htmlFor={inputId}
              className={
                room > 0 && !upload.isPending
                  ? "inline-flex h-10 w-fit cursor-pointer items-center rounded-md border border-border bg-card px-4 text-sm font-medium text-foreground hover:bg-accent"
                  : "inline-flex h-10 w-fit items-center rounded-md border border-border bg-card px-4 text-sm font-medium text-muted-foreground opacity-50"
              }
            >
              {upload.isPending ? "Uploading…" : "Add photos"}
            </label>
            <input
              id={inputId}
              type="file"
              accept={PHOTO_TYPES.join(",")}
              multiple
              disabled={room <= 0 || upload.isPending}
              onChange={choose}
              className="sr-only"
            />
            <p className="text-xs text-muted-foreground">
              Up to {MAX_PHOTOS_PER_CARD} photos per card (JPEG, PNG or WebP, 5 MB each).{" "}
              {room <= 0 ? "Remove a photo to add another." : `${String(room)} more can be added.`}
            </p>
          </div>
        )}
      </section>
    </Card>
  );
}
